import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, ClipboardList, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api, { apiError } from "../lib/api";

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-navy outline-none focus:border-saffron";
const TICKET_CATEGORIES = ["Shipment Delay", "Pickup Issue", "Delivery Issue", "Tracking Issue",
  "Address Correction", "Billing", "International Query", "Account Issue", "Technical Issue", "Other"];
const CLAIM_TYPES = ["Lost", "Damaged", "Delayed", "Missing Contents", "Other"];
const CLAIM_STEPS = ["Submit Claim", "Document Verification", "Investigation", "Decision", "Resolution"];

export default function Support() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "claim" ? "claim" : "ticket");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [contact, setContact] = useState(null);
  const [ticket, setTicket] = useState({ name: "", phone: "", email: "", awb: "", category: "Shipment Delay", description: "" });
  const [claim, setClaim] = useState({ awb: "", booking_date: "", sender: "", receiver: "", issue_type: "Lost", description: "", phone: "", email: "" });
  const [check, setCheck] = useState({ id: "", phone: "" });
  const [status, setStatus] = useState(null);

  useEffect(() => { api.get("/public/home").then((r) => setContact(r.data.contact)).catch(() => {}); }, []);

  const setT = (k) => (e) => setTicket({ ...ticket, [k]: e.target.value });
  const setC = (k) => (e) => setClaim({ ...claim, [k]: e.target.value });

  const submitTicket = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/public/support-tickets", ticket);
      setDone({ kind: "Ticket", id: res.data.ticket_id });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const submitClaim = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/public/claims", claim);
      setDone({ kind: "Claim", id: res.data.claim_id });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const checkStatus = async (e) => {
    e.preventDefault();
    setStatus(null);
    const isClaim = check.id.toUpperCase().startsWith("CLM");
    try {
      const res = await api.get(`/public/${isClaim ? "claims" : "support-tickets"}/${encodeURIComponent(check.id)}?phone=${encodeURIComponent(check.phone)}`);
      setStatus(res.data);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="support-page">
      <Navbar />
      <div className="bg-navy py-14">
        <div className="mx-auto max-w-3xl px-6">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            {tab === "claim" ? "Need to Raise a Shipment Claim?" : "We're Here to Help"}
          </h1>
          <div className="mt-4 h-1 w-24 rounded-full bg-gradient-to-r from-saffron via-white to-green-600" />
          <p className="mt-4 text-sm text-slate-300">
            {tab === "claim"
              ? "Submit claims for eligible shipment issues. Every claim goes through: Submit → Document Verification → Investigation → Decision → Resolution."
              : "Raise a support request for shipment delays, pickup or delivery issues, tracking, billing or account queries."}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="mb-6 flex gap-2" data-testid="support-tabs">
          {[["ticket", "Support Ticket"], ["claim", "Raise a Claim"]].map(([t, l]) => (
            <button key={t} onClick={() => { setTab(t); setDone(null); setStatus(null); }} data-testid={`support-tab-${t}`}
                    className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${tab === t ? "bg-saffron text-white" : "border border-slate-300 text-slate-600 hover:border-saffron hover:text-saffron"}`}>
              {l}
            </button>
          ))}
        </div>

        {done ? (
          <div className="rounded-2xl border border-emerald-200 bg-white p-10 text-center" data-testid="support-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
            <h2 className="mt-4 font-heading text-2xl font-bold text-navy">{done.kind} Submitted</h2>
            <p className="mt-2 text-sm text-slate-500">Save your reference number to check status anytime:</p>
            <p className="mt-3 font-mono text-2xl font-extrabold text-express" data-testid="support-ref-id">{done.id}</p>
            <p className="mt-3 text-xs text-slate-400">Our team will reach you on your given phone number.</p>
            <button onClick={() => setDone(null)} data-testid="support-new-btn"
                    className="mt-6 rounded-full border border-slate-300 px-5 py-2 text-sm font-bold text-navy hover:bg-slate-100">
              Submit Another
            </button>
          </div>
        ) : tab === "ticket" ? (
          <form onSubmit={submitTicket} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="ticket-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <input required className={inputCls} data-testid="ticket-name" placeholder="Full name" value={ticket.name} onChange={setT("name")} />
              <input required className={inputCls} data-testid="ticket-phone" placeholder="Phone" value={ticket.phone} onChange={setT("phone")} />
              <input type="email" className={inputCls} data-testid="ticket-email" placeholder="Email (optional)" value={ticket.email} onChange={setT("email")} />
              <input className={inputCls} data-testid="ticket-awb" placeholder="Tracking / AWB number (optional)" value={ticket.awb} onChange={setT("awb")} />
              <select className={`${inputCls} sm:col-span-2`} data-testid="ticket-category" value={ticket.category} onChange={setT("category")}>
                {TICKET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <textarea required rows={4} className={`${inputCls} sm:col-span-2`} data-testid="ticket-description" placeholder="Describe your issue…" value={ticket.description} onChange={setT("description")} />
            </div>
            <button disabled={busy} data-testid="ticket-submit-btn"
                    className="mt-5 flex items-center gap-2 rounded-full bg-saffron px-6 py-3 text-sm font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />} Submit Ticket
            </button>
          </form>
        ) : (
          <form onSubmit={submitClaim} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="claim-form">
            <div className="mb-5 flex flex-wrap items-center gap-1.5" data-testid="claim-steps">
              {CLAIM_STEPS.map((s, i) => (
                <span key={s} className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                  <span className="grid h-5 w-5 place-items-center rounded-full bg-navy text-[10px] text-white">{i + 1}</span>{s}
                  {i < CLAIM_STEPS.length - 1 && <span className="text-slate-300">→</span>}
                </span>
              ))}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <input required className={inputCls} data-testid="claim-awb" placeholder="Tracking / AWB number" value={claim.awb} onChange={setC("awb")} />
              <input type="date" className={inputCls} data-testid="claim-date" value={claim.booking_date} onChange={setC("booking_date")} />
              <input className={inputCls} data-testid="claim-sender" placeholder="Sender name" value={claim.sender} onChange={setC("sender")} />
              <input className={inputCls} data-testid="claim-receiver" placeholder="Receiver name" value={claim.receiver} onChange={setC("receiver")} />
              <select className={inputCls} data-testid="claim-issue" value={claim.issue_type} onChange={setC("issue_type")}>
                {CLAIM_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input required className={inputCls} data-testid="claim-phone" placeholder="Contact phone" value={claim.phone} onChange={setC("phone")} />
              <input type="email" className={`${inputCls} sm:col-span-2`} data-testid="claim-email" placeholder="Email (optional)" value={claim.email} onChange={setC("email")} />
              <textarea required rows={4} className={`${inputCls} sm:col-span-2`} data-testid="claim-description" placeholder="Describe the issue in detail…" value={claim.description} onChange={setC("description")} />
            </div>
            <p className="mt-3 text-xs text-slate-400">Claims are processed as per the applicable courier/service terms. Keep your invoice and packing photos ready — our team may request them during document verification.</p>
            <button disabled={busy} data-testid="claim-submit-btn"
                    className="mt-5 flex items-center gap-2 rounded-full bg-saffron px-6 py-3 text-sm font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardList className="h-4 w-4" />} Submit Claim
            </button>
          </form>
        )}

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="status-checker">
          <h3 className="flex items-center gap-2 font-heading text-base font-bold text-navy"><Search className="h-4 w-4 text-saffron" /> Check Ticket / Claim Status</h3>
          <form onSubmit={checkStatus} className="mt-4 grid gap-3 sm:grid-cols-3">
            <input required className={inputCls} data-testid="status-id" placeholder="TKT-XXXXXX or CLM-XXXXXX" value={check.id} onChange={(e) => setCheck({ ...check, id: e.target.value })} />
            <input required className={inputCls} data-testid="status-phone" placeholder="Phone used while submitting" value={check.phone} onChange={(e) => setCheck({ ...check, phone: e.target.value })} />
            <button className="rounded-lg bg-navy px-4 py-2.5 text-sm font-bold text-white hover:bg-express" data-testid="status-check-btn">Check Status</button>
          </form>
          {status && (
            <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm" data-testid="status-result">
              <div className="flex justify-between"><span className="text-slate-500">Reference</span><b className="font-mono text-navy">{status.ticket_id || status.claim_id}</b></div>
              <div className="mt-1 flex justify-between"><span className="text-slate-500">Status</span><b className="text-saffron">{status.status}</b></div>
              {status.note && <div className="mt-1 flex justify-between gap-4"><span className="text-slate-500">Team note</span><span className="text-right text-navy">{status.note}</span></div>}
              {status.responses?.length > 0 && (
                <div className="mt-3 border-t border-slate-200 pt-3" data-testid="status-responses">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Responses from INDIAFAST</p>
                  {status.responses.map((res, i) => (
                    <div key={i} className="mt-2 rounded-lg bg-white p-3" data-testid={`status-response-${i}`}>
                      <p className="text-sm text-navy">{res.message}</p>
                      <p className="mt-1 text-[11px] text-slate-400">{res.by || "Support Team"} · {(res.at || "").slice(0, 16).replace("T", " ")}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer contact={contact} />
    </div>
  );
}
